// src/components/LineupsSection.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api"; // ✅ env-based axios client
import styles from "../styles/FixturePage.module.css";

const normalizeLineupsPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.response)) return payload.response;
  if (Array.isArray(payload?.lineups)) return payload.lineups;
  return [];
};

const playerPhotoUrl = (player) => {
  if (player?.photo) return player.photo;
  if (player?.id) return `https://media.api-sports.io/football/players/${player.id}.png`;
  return null;
};

const LineupsSection = ({ fixtureId, fallbackLineups = null }) => {
  const [lineups, setLineups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [predicted, setPredicted] = useState(false);

  useEffect(() => {
    if (!fixtureId) return;

    const fetchLineups = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("/football/lineups", {
          params: { fixture_id: fixtureId },
        });
        const primary = normalizeLineupsPayload(res.data);
        setPredicted(Boolean(res.data?.predicted));
        if (primary.length) {
          setLineups(primary);
        } else {
          setLineups(normalizeLineupsPayload(fallbackLineups));
        }
      } catch (err) {
        console.error("Error fetching lineups:", err);
        const fallback = normalizeLineupsPayload(fallbackLineups);
        if (fallback.length) {
          setLineups(fallback);
          setError("");
        } else {
          setError("Failed to load lineups.");
          setLineups([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLineups();
  }, [fixtureId, fallbackLineups]);

  if (loading) return <div className={styles.matchEmpty}>Loading lineups...</div>;
  if (error) return <div className={styles.matchEmpty}>{error}</div>;
  if (!lineups.length) return <div className={styles.matchEmpty}>Confirmed lineups are not available yet.</div>;

  const renderPlayer = (p, isSub = false) => {
    const pos = p.player?.pos || "?";
    const photo = playerPhotoUrl(p.player);
    const playerId = p.player?.id;
    const content = (
      <>
        {photo ? (
          <>
            <img
              src={photo}
              alt={p.player.name}
              className={styles.playerPhotoSmall}
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                const fallback = e.currentTarget.nextElementSibling;
                if (fallback) fallback.style.display = "grid";
              }}
            />
            <span className={`${styles.playerNumberFallback} ${styles.playerNumberFallbackHidden}`}>
              {p.player?.number || "·"}
            </span>
          </>
        ) : (
          <span className={styles.playerNumberFallback}>{p.player?.number || "·"}</span>
        )}
        <div className={styles.pitchPlayerLabel}>
          <b>{p.player?.number ? `${p.player.number} ` : ""}{p.player?.name}</b>
          {isSub && <span>{pos}</span>}
        </div>
      </>
    );

    const className = `${styles.playerMiniCard} ${isSub ? styles.subPlayer : ""}`;
    if (playerId) {
      return (
        <Link
          key={playerId}
          to={`/player/${playerId}?fixture_id=${fixtureId}`}
          className={`${className} ${styles.lineupPlayerLink}`}
          aria-label={`View ${p.player?.name || "player"} stats`}
        >
          {content}
        </Link>
      );
    }

    return (
      <div key={p.player?.name} className={className}>{content}</div>
    );
  };

  return (
    <section className={styles.lineupsPanel}>
      <div className={styles.confirmedLineups}>
        <div>
          <span className={styles.confirmedCheck}>✓</span>
          <div>
            <span className={styles.sectionEyebrow}>Team news</span>
            <h2>{predicted ? "Predicted lineups" : "Confirmed lineups"}</h2>
            {predicted && <small>Previous XI adjusted using the current injury list</small>}
          </div>
        </div>
        <span>{lineups.length} teams</span>
      </div>
      {lineups.map((team, i) => (
        <div key={i} className={styles.lineupBlock}>
          <div className={styles.lineupTeamHeader}>
            {team.team?.logo && <img src={team.team.logo} alt="" />}
            <div>
              <strong>{team.team?.name}</strong>
              <span>{team.formation || "Formation pending"}</span>
            </div>
          </div>

          <div className={styles.formationPitch}>
            <div className={styles.pitchMarkings} aria-hidden="true" />
            {["G", "D", "M", "F"].map((pos) => {
              const starters = team.startXI?.filter(
                (p) => p.player?.pos === pos
              );
              if (!starters?.length) return null;
              return (
                <div key={pos} className={styles.lineupRow}>
                  {starters.map((p) => renderPlayer(p))}
                </div>
              );
            })}
          </div>

          {team.substitutes?.length > 0 && (
            <div className={styles.substitutesSection}>
              <h3>Substitutes</h3>
              <div className={styles.subsList}>
                {team.substitutes.map((p) => renderPlayer(p, true))}
              </div>
            </div>
          )}

          {team.substitutions?.length > 0 && (
            <div className={styles.substitutionList}>
              <h3>Changes</h3>
              <ul>
                {team.substitutions.map((s, idx) => (
                  <li key={idx}>
                    <span>{s.playerIn?.name}</span>
                    <small>for {s.playerOut?.name} · {s.minute}'</small>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </section>
  );
};

export default LineupsSection;
